import { useEffect, useState } from "react";
import { PhotoStage, PhotoControls, usePhotoGestures } from "@/components/PhotoStage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { AuthImg } from "@/components/AuthImg";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PhotoFixAction } from "@/components/PhotoFixAction";
import { MainPhotoAction } from "@/components/MainPhotoAction";
import { MainPhotoStatus } from "@/components/MainPhotoStatus";
import { ImageOff, ChevronLeft, ChevronRight, Maximize2, Star } from "lucide-react";

// THE PHOTO WORKSPACE: a big standing photo, a thumbnail strip under it, and full-screen on tap.
//
// It used to carry its own copy of the zoom/rotate/pan code AND its own full-screen dialog. Both
// are gone: the standing photo is a `PhotoStage` and full-screen is the one `PhotoLightbox` every
// other screen opens (his instruction, 2026-10-15 — *"one implementation, not several"*).
//   photos         [{file_id, is_archived}, ...]
//   uploadEndpoint optional — renders an "Add photos" uploader below the strip
//   testidPrefix   keep "identify" on the Identify page so its tests are unchanged
//   heightClass    preview height (defaults to the tall workspace height)
export function PhotoViewer({
  photos,
  uploadEndpoint,
  onUploaded,
  onUploadFailed,
  cameraFirst = true,
  uploadLabel = "Add photos",
  testidPrefix = "photo-viewer",
  heightClass = "h-[42dvh] lg:h-[74dvh]",
  fixScope,            // "donor" | "part" — turns on the shared wrong-photo action
  fixRecordId,
  onFixed,
  main,                // { scope, recordId, chosen, resolved, onChanged } — the shared main-photo action in the viewer
}) {
  const list = (photos || []).filter((p) => p && p.file_id && !p.is_archived);
  const ids = list.map((p) => p.file_id);
  const [mainIdx, setMainIdx] = useState(0);
  const [boxIdx, setBoxIdx] = useState(null);
  const g = usePhotoGestures(ids[mainIdx]);

  useEffect(() => { if (mainIdx > list.length - 1) setMainIdx(0); }, [list.length, mainIdx]);
  const mainStep = (d) => setMainIdx((i) => (i + d + ids.length) % ids.length);
  // a PERSON's pick, still live: the chosen id is the one the server resolved (an archived pick stops winning)
  const picked = (fid) => !!main?.chosen && main.chosen === fid && main.resolved === fid;
  // ONLY A PHOTO THAT ACTUALLY SHOWED can stand for the record (2 Nov): a missing or failed file gets no main-photo button
  const [shown, setShown] = useState({});
  const seen = (fid) => (s) => setShown((m) => (m[fid] === s ? m : { ...m, [fid]: s }));
  const mainAction = (fid, extra) => main?.recordId && fid ? (
    <MainPhotoAction scope={main.scope} recordId={main.recordId} fileId={fid} isMain={main.resolved === fid}
      isOverride={picked(fid)} onChanged={main.onChanged} {...extra} />
  ) : null;

  return (
    <div data-testid={`${testidPrefix}-photo-workspace`}>
      {list.length === 0 ? (
        <div data-testid={`${testidPrefix}-photos-empty`} className={`flex flex-col items-center justify-center gap-2 text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-2xl ${heightClass}`}>
          <ImageOff className="w-8 h-8" /><span className="text-sm font-medium">No photos yet</span>
        </div>
      ) : (
        <div data-testid={`${testidPrefix}-main-preview`} className={`relative w-full ${heightClass} rounded-2xl bg-slate-900 border border-slate-200 overflow-hidden`}>
          <PhotoStage fileId={ids[mainIdx]} alt={`photo ${mainIdx + 1}`} gestures={g}
            missing={!!list[mainIdx]?.bytes_missing} onState={seen(`s:${ids[mainIdx]}`)}
            controls={false} testidPrefix={`${testidPrefix}-inline`} />
          <span className="absolute top-3 left-3 text-[11px] font-mono px-2 py-1 rounded-full bg-slate-900/70 text-white pointer-events-none">{mainIdx + 1} / {ids.length}</span>
          {ids.length > 1 && (
            <>
              <button data-testid={`${testidPrefix}-main-prev`} onClick={(e) => { e.stopPropagation(); mainStep(-1); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/85 border border-slate-200 text-slate-700 hover:bg-white shadow">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid={`${testidPrefix}-main-next`} onClick={(e) => { e.stopPropagation(); mainStep(1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/85 border border-slate-200 text-slate-700 hover:bg-white shadow">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          {fixScope && fixRecordId && list[mainIdx] && (
            <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
              <PhotoFixAction scope={fixScope} recordId={fixRecordId} photo={list[mainIdx]}
                testid={`${testidPrefix}-fix`} onDone={onFixed} />
            </div>
          )}
          {main?.recordId && ids[mainIdx] && shown[`s:${ids[mainIdx]}`] === "loaded" && (
            // under the photo counter, clear of the zoom/rotate row along the bottom on every screen size
            <div className="absolute z-10 top-12 left-3 rounded-lg bg-white/95 p-1 shadow" onClick={(e) => e.stopPropagation()}>
              {mainAction(ids[mainIdx], { testid: `${testidPrefix}-stage-main` })}
            </div>
          )}
          <div className="absolute bottom-3 right-3 flex gap-2 items-center">
            <PhotoControls g={g} testidPrefix={`${testidPrefix}-inline`} />
            <button data-testid={`${testidPrefix}-open-lightbox`} onClick={(e) => { e.stopPropagation(); setBoxIdx(mainIdx); }}
              title="Full screen"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow border border-slate-200/60">
              <Maximize2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {list.length > 0 && (
        <div data-testid={`${testidPrefix}-thumb-strip`} className="flex flex-wrap gap-3 mt-3">
          {list.map((p, i) => (
            <div key={p.file_id} className="relative group">
              <button data-testid={`${testidPrefix}-thumb-${i}`} onClick={() => setMainIdx(i)}
                className={`block w-24 h-24 lg:w-32 lg:h-32 rounded-xl overflow-hidden bg-slate-50 transition-all ${i === mainIdx ? "ring-2 ring-[#E0781A] border border-[#E0781A]" : "border border-slate-200 hover:border-[#E0781A]"}`}>
                <AuthImg fileId={p.file_id} thumb missing={!!p.bytes_missing} onState={seen(`t:${p.file_id}`)} className="w-full h-full object-cover" alt={`thumb ${i + 1}`} />
              </button>
              {picked(p.file_id) && (
                <span data-testid={`${testidPrefix}-thumb-is-main-${i}`}
                  className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-[#E0781A] text-white pointer-events-none">
                  <Star className="w-2.5 h-2.5 fill-current" /> Main
                </span>
              )}
              {main?.recordId && !picked(p.file_id) && shown[`t:${p.file_id}`] === "loaded" && (
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                  {mainAction(p.file_id, { compact: true, testid: `${testidPrefix}-thumb-main-${i}` })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {main?.recordId && main.scope === "part" && (
        <MainPhotoStatus picked={!!main.chosen && main.chosen === main.resolved && ids.includes(main.chosen)}
          hasPhotos={list.length > 0} testid={`${testidPrefix}-main-status`} />
      )}

      {uploadEndpoint && (
        <div className="mt-4" data-testid={`${testidPrefix}-photo-upload`}>
          <PhotoUploader endpoint={uploadEndpoint} cameraFirst={cameraFirst} onUploaded={onUploaded} onFailed={onUploadFailed} buttonLabel={uploadLabel} testidPrefix={`${testidPrefix}-photo`} />
        </div>
      )}

      {/* FULL SCREEN IS THE ONE VIEWER — the same one the truck page and every thumbnail opens. */}
      <PhotoLightbox photos={list} index={boxIdx} onIndex={setBoxIdx} onClose={() => setBoxIdx(null)}
        testidPrefix={`${testidPrefix}-photos`}
        fixScope={fixScope} fixRecordId={fixRecordId} onFixed={onFixed}
        mainScope={main?.scope} mainRecordId={main?.recordId} mainPhotoFileId={main?.chosen}
        resolvedMainFileId={main?.resolved} onMainChanged={main?.onChanged} />
    </div>
  );
}
